import { Play, Pause, Square, Copy, FileText, FileType, Trash2, Keyboard } from 'lucide-react';
import { Button } from './ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

export const ControlPanel = ({
  t,
  isListening,
  isPaused,
  isSupported,
  onStart,
  onPause,
  onResume,
  onStop,
  onCopy,
  onExportTxt,
  onExportRtf,
  onClear,
  hasTranscript,
}) => {
  return (
    <div className="flex flex-col gap-4">
      {/* Main Controls */}
      <div className="flex flex-wrap items-center justify-center gap-3">
        {!isListening ? (
          <Button
            size="lg"
            onClick={onStart}
            disabled={!isSupported}
            className="gap-2 min-w-[120px]"
            data-testid="start-button"
          >
            <Play className="h-5 w-5" />
            {t('btn_start')}
          </Button>
        ) : (
          <>
            {isPaused ? (
              <Button
                size="lg"
                onClick={onResume}
                className="gap-2 min-w-[120px]"
                data-testid="resume-button"
              >
                <Play className="h-5 w-5" />
                {t('btn_resume')}
              </Button>
            ) : (
              <Button
                size="lg"
                variant="secondary"
                onClick={onPause}
                className="gap-2 min-w-[120px]"
                data-testid="pause-button"
              >
                <Pause className="h-5 w-5" />
                {t('btn_pause')}
              </Button>
            )}
            <Button
              size="lg"
              variant="destructive"
              onClick={onStop}
              className="gap-2 min-w-[120px]"
              data-testid="stop-button"
            >
              <Square className="h-5 w-5" />
              {t('btn_stop')}
            </Button>
          </>
        )}
      </div>

      {/* Keyboard Shortcuts Hint */}
      <div className="flex justify-center">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button 
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                data-testid="keyboard-shortcuts-hint"
              >
                <Keyboard className="h-3.5 w-3.5" />
                {t('keyboard_shortcuts')}
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              <div className="space-y-1">
                <p>{t('keyboard_space')}</p>
                <p>{t('keyboard_escape')}</p>
                <p>{t('keyboard_copy')}</p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Export Controls */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onCopy}
          disabled={!hasTranscript}
          className="gap-2"
          data-testid="copy-button"
        >
          <Copy className="h-4 w-4" />
          {t('btn_copy')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onExportTxt}
          disabled={!hasTranscript}
          className="gap-2"
          data-testid="export-txt-button"
        >
          <FileText className="h-4 w-4" />
          {t('btn_export_txt')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onExportRtf}
          disabled={!hasTranscript}
          className="gap-2"
          data-testid="export-rtf-button"
        >
          <FileType className="h-4 w-4" />
          {t('btn_export_rtf')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onClear}
          disabled={!hasTranscript}
          className="gap-2 text-destructive hover:text-destructive"
          data-testid="clear-button"
        >
          <Trash2 className="h-4 w-4" />
          {t('btn_clear')}
        </Button>
      </div>
    </div>
  );
};
